import { Ban } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ClassificationTypeBadge } from '../ClassificationTypeBadge';
import { cn } from '@/lib/utils';

interface GICSItemRowProps {
  code: string;
  type: 'Sector' | 'Industry' | 'Sub-Industry';
  typeLabel: string;
  name: string;
  subtitle: string;
  isExcluded: boolean;
  /** Some children excluded but not the whole item — Figma "Partial". */
  isPartiallyExcluded?: boolean;
  /** Disabled by the Cyclicality selector — visible, greyed, not selectable. */
  isDisabled?: boolean;
  /** Holds constituents in the selected universe today. */
  isLive?: boolean;
  onClick: () => void;
}

export function GICSItemRow({
  code,
  typeLabel,
  name,
  subtitle,
  isExcluded,
  isPartiallyExcluded = false,
  isDisabled = false,
  isLive = false,
  onClick,
}: GICSItemRowProps) {
  return (
    // Figma: rows are px-4 py-2.5; a mono code Badge + a secondary type
    // Badge lead the row; excluded rows tint destructive, partially
    // excluded tint amber; a right-aligned status Badge (Excluded /
    // Partial) or hover-only "Exclude" affordance closes the row.
    <button
      type="button"
      disabled={isDisabled}
      onClick={onClick}
      className={cn(
        'group flex items-center justify-between gap-3 w-full px-4 py-2.5 text-left transition-colors',
        isDisabled
          ? 'bg-muted/20 opacity-60 cursor-not-allowed'
          : isExcluded
            ? 'bg-destructive/[0.08] hover:bg-destructive/[0.12]'
            : isPartiallyExcluded
              ? 'bg-amber-500/5 hover:bg-amber-500/10'
              : 'hover:bg-accent/50',
      )}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <Badge
          variant="outline"
          className="font-mono text-xs shrink-0 tabular-nums"
        >
          {code}
        </Badge>
        <ClassificationTypeBadge label={typeLabel} isLive={isLive} />
        <div className="min-w-0 flex-1">
          <p
            className={cn(
              'text-sm truncate',
              isExcluded
                ? 'text-destructive/80'
                : isPartiallyExcluded
                  ? 'text-amber-600/80'
                  : '',
            )}
          >
            {name}
          </p>
          <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
        </div>
      </div>
      <div className="ml-3 shrink-0">
        {isExcluded ? (
          <Badge
            variant="outline"
            className="text-xs px-2 py-0.5 bg-destructive/10 text-destructive border-destructive/30"
          >
            Excluded
          </Badge>
        ) : isPartiallyExcluded ? (
          <Badge
            variant="outline"
            className="text-xs px-2 py-0.5 bg-amber-500/10 text-amber-600 border-amber-500/30"
          >
            Partial
          </Badge>
        ) : (
          <span className="text-xs text-muted-foreground opacity-0 group-hover:opacity-60 transition-opacity flex items-center gap-1">
            <Ban className="w-3 h-3" /> Exclude
          </span>
        )}
      </div>
    </button>
  );
}
